import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import http, { httpSymfony } from 'Utils/http';
import { cachedGet } from 'Utils/cachedGet';
import { usePolling } from 'Hooks/usePolling';
import { useDispatch, useSelector } from 'react-redux';
import { setLoading } from 'Actions/loading';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Button,
    Chip,
    Tabs,
    Tab,
    Typography,
    TextField,
    Link,
    MenuItem,
} from '@mui/material';
import { PaginationBar } from 'Shared/PaginationBar';
import { useSnackbar } from 'notistack';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import FlowImportFlow from 'Shared/flows/flows-interface/FlowImportFlow';
import { downloadIfAttachment, readAxiosData } from 'Utils/utils';
import { process } from 'Utils/errorHandler';

const STATUS_COLOR = {
    running: 'info',
    queued: 'default',
    completed: 'success',
    failed: 'error',
    cancelled: 'default',
    expired: 'warning',
};

const SORTABLE = {
    started_at: 'Started At',
    status: 'Status',
    execution_time: 'Execution Time',
};

const STOPPABLE = ['running', 'expired'];

const STATUS_TABS = [
    { value: 'all', label: 'All' },
    { value: 'running', label: 'Running' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
    { value: 'cancelled', label: 'Cancelled' },
];

const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) {
        return '-';
    }
    const total = Math.max(0, Math.floor(seconds));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) {
        return `${h}h ${m}m ${s}s`;
    }
    if (m > 0) {
        return `${m}m ${s}s`;
    }
    return `${s}s`;
};

const SEARCH_DELAY_MS = 400;

const BackgroundFlowsPage = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const currentUser = useSelector((state) => state.auth.user);
    const canFilterByUser = currentUser?.access === 'Admin' || currentUser?.access === 'Company Admin';

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [records, setRecords] = useState([]);
    const [sortBy, setSortBy] = useState('started_at');
    const [sortDir, setSortDir] = useState('desc');
    const [statusFilter, setStatusFilter] = useState('running');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [userFilter, setUserFilter] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [users, setUsers] = useState([]);

    const [bgVisible, setBgVisible] = useState(false);
    const [bgTitle, setBgTitle] = useState('Flow Response');
    const [bgResponse, setBgResponse] = useState('');
    const [bgStatus, setBgStatus] = useState(null);
    const [bgItem, setBgItem] = useState(null);
    const [bgIsBackground, setBgIsBackground] = useState(false);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(column);
            setSortDir('desc');
        }
        setPage(0);
    };

    const handleStatusTab = (event, value) => {
        setStatusFilter(value);
        setPage(0);
    };

    const fetchData = useCallback(
        (showLoader) => {
            if (showLoader) {
                dispatch(setLoading(true));
            }

            const pagination = encodeURIComponent(
                JSON.stringify({ pageSize: rowsPerPage, currentPage: page })
            );
            const statusParam = statusFilter && statusFilter !== 'all' ? `&status=${statusFilter}` : '';
            const dateFromParam = dateFrom ? `&date_from=${dateFrom}` : '';
            const dateToParam = dateTo ? `&date_to=${dateTo}` : '';
            const userParam = userFilter ? `&user_id=${userFilter}` : '';
            const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
            const url = `/user/background/flows?pagination=${pagination}&sort_by=${sortBy}&sort_dir=${sortDir}${statusParam}${dateFromParam}${dateToParam}${userParam}${searchParam}`;

            http.get(url)
                .then((res) => {
                    setRecords(res.data.records || []);
                    setTotalRecords(res.data.pagination?.totalRecords || 0);
                    if (showLoader) {
                        dispatch(setLoading(false));
                    }
                })
                .catch((err) => {
                    if (showLoader) {
                        dispatch(setLoading(false));
                    }
                    enqueueSnackbar(`${err.response?.data?.message || 'Error loading background flows'}`, {
                        action: CustomSnackbar,
                        variant: 'error',
                    });
                });
        },
        [dispatch, enqueueSnackbar, page, rowsPerPage, sortBy, sortDir, statusFilter, dateFrom, dateTo, userFilter, search]
    );

    const cancelFlow = (guid) => {
        httpSymfony
            .post(`/flow/${guid}/cancel`)
            .then(() => {
                enqueueSnackbar('Cancellation requested', { action: CustomSnackbar, variant: 'success' });
                fetchData(false);
            })
            .catch((err) => {
                enqueueSnackbar(`${err.response?.data?.message || 'Unable to cancel flow'}`, {
                    action: CustomSnackbar,
                    variant: 'error',
                });
            });
    };

    // Fetch the result of a background flow from Symfony (same mechanism as the long-running
    // flow modal in FlowTools): read straight from `/get/result/{flow_id}-{guid}` and show it.
    const fetchGetData = useCallback(
        async (item) => {
            if (!item) {
                return;
            }
            dispatch(setLoading(true));
            try {
                const res = await httpSymfony.get(`/get/result/${item.flow_id}-${item.guid}`, {
                    responseType: 'blob',
                });
                if (downloadIfAttachment(res)) {
                    setBgVisible(false);
                    setBgIsBackground(false);
                    enqueueSnackbar('The file has been downloaded', { action: CustomSnackbar, variant: 'success' });
                    return;
                }
                const data = await readAxiosData(res.data);
                const stillBackground = res.status === 202 || (data && Number(data.status) === 202);
                setBgItem(item);
                setBgTitle(stillBackground ? 'Running in the background' : 'Flow Response');
                setBgStatus(res.status);
                setBgResponse(JSON.stringify(data));
                setBgIsBackground(stillBackground);
                setBgVisible(true);
                if (stillBackground) {
                    enqueueSnackbar('The flow is still running. Please try again in a moment.', {
                        action: CustomSnackbar,
                        variant: 'info',
                    });
                }
            } catch (err) {
                setBgItem(item);
                setBgTitle('Flow Response');
                if (err.response) {
                    // `responseType: 'blob'` leaves the body a Blob, but errorHandler reads
                    // `data.message` — parse it first or it falls back to a generic message.
                    err.response.data = await readAxiosData(err.response.data);
                    setBgStatus(err.response.status);
                    setBgResponse(JSON.stringify(err.response.data));
                } else {
                    setBgStatus(0);
                    setBgResponse(JSON.stringify({ error: err.message || 'Network error' }));
                }
                setBgIsBackground(false);
                setBgVisible(true);
                process(err, enqueueSnackbar);
            } finally {
                dispatch(setLoading(false));
            }
        },
        [dispatch, enqueueSnackbar]
    );

    useEffect(() => {
        if (!canFilterByUser) {
            return;
        }
        cachedGet('/users')
            .then((res) => setUsers(Array.isArray(res.data?.users) ? res.data.users : []))
            .catch(() => setUsers([]));
    }, [canFilterByUser]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(0);
        }, SEARCH_DELAY_MS);

        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        fetchData(true);
    }, [fetchData]);

    usePolling(() => fetchData(false));

    return (
        <>
            <FlowImportFlow
                name={bgItem?.flow_name || bgItem?.guid || 'result'}
                title={bgTitle}
                visible={bgVisible}
                setVisible={setBgVisible}
                response={bgResponse}
                setResponse={setBgResponse}
                status={bgStatus}
                setStatus={setBgStatus}
                simpleVersion={false}
                isBackground={bgIsBackground}
                onGetResult={() => fetchGetData(bgItem)}
                onStop={bgItem ? () => { cancelFlow(bgItem.guid); setBgIsBackground(false); } : undefined}
                onBackgroundReset={() => setBgIsBackground(false)}
            />
            <Typography
                variant="h1"
                sx={{
                    textAlign: 'center',
                    marginBottom: '25px',
                    fontSize: '3rem',
                    fontWeight: 500,
                }}
            >
                Background Flows
            </Typography>
            <Box component={Paper} sx={{ padding: '20px 20px 0px 20px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <Tabs
                        value={statusFilter}
                        onChange={handleStatusTab}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        {STATUS_TABS.map((tab) => (
                            <Tab key={tab.value} value={tab.value} label={tab.label} />
                        ))}
                    </Tabs>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <TextField
                            size="small"
                            label="Search"
                            placeholder="Flow name"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            sx={{ minWidth: 180 }}
                        />
                        {canFilterByUser && (
                            <TextField
                                select
                                size="small"
                                label="User"
                                value={userFilter}
                                onChange={(e) => { setUserFilter(e.target.value); setPage(0); }}
                                sx={{ minWidth: 180 }}
                            >
                                <MenuItem value="">All users</MenuItem>
                                {users.map((u) => (
                                    <MenuItem key={u.id} value={u.id}>
                                        {[`${u.first_name || ''} ${u.last_name || ''}`.trim(), u.email && `(${u.email})`].filter(Boolean).join(' ')}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                        <TextField
                            type="date"
                            size="small"
                            label="From"
                            InputLabelProps={{ shrink: true }}
                            value={dateFrom}
                            onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
                        />
                        <TextField
                            type="date"
                            size="small"
                            label="To"
                            InputLabelProps={{ shrink: true }}
                            value={dateTo}
                            onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
                        />
                        <Button color="primary" variant="outlined" onClick={() => fetchData(true)}>
                            Refresh
                        </Button>
                    </Box>
                </Box>

                <TableContainer>
                    <Table sx={{ minWidth: 650 }} size="medium">
                        <TableHead>
                            <TableRow
                                sx={{
                                    height: '50px',
                                    '& .MuiTableCell-root': {
                                        fontSize: '1.2rem',
                                    },
                                }}
                            >
                                <TableCell align="center">Flow Name</TableCell>
                                <TableCell align="center">Flow ID</TableCell>
                                <TableCell align="center">Run by / Owner</TableCell>
                                <TableCell align="center" sortDirection={sortBy === 'status' ? sortDir : false}>
                                    <TableSortLabel
                                        active={sortBy === 'status'}
                                        direction={sortBy === 'status' ? sortDir : 'asc'}
                                        onClick={() => handleSort('status')}
                                    >
                                        {SORTABLE.status}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="center" sortDirection={sortBy === 'started_at' ? sortDir : false}>
                                    <TableSortLabel
                                        active={sortBy === 'started_at'}
                                        direction={sortBy === 'started_at' ? sortDir : 'asc'}
                                        onClick={() => handleSort('started_at')}
                                    >
                                        {SORTABLE.started_at}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="center" sortDirection={sortBy === 'execution_time' ? sortDir : false}>
                                    <TableSortLabel
                                        active={sortBy === 'execution_time'}
                                        direction={sortBy === 'execution_time' ? sortDir : 'asc'}
                                        onClick={() => handleSort('execution_time')}
                                    >
                                        {SORTABLE.execution_time}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="center">GUID</TableCell>
                                <TableCell align="center" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {records.map((item) => (
                                <TableRow
                                    key={item.guid}
                                    sx={{
                                        '& .MuiTableCell-root': {
                                            fontSize: '1.2rem',
                                        },
                                        '&:last-child td, &:last-child th': { border: 0 },
                                    }}
                                >
                                    <TableCell align="center">{item.flow_name || '-'}</TableCell>
                                    <TableCell align="center">{item.flow_id}</TableCell>
                                    <TableCell align="center">
                                        {(item.user_id || item.owner_id) ? (
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px', flexWrap: 'wrap' }}>
                                                {item.user_id ? (
                                                    <Link
                                                        component={RouterLink}
                                                        to={`/users/${item.user_id}`}
                                                        sx={{ fontSize: '1.2rem' }}
                                                    >
                                                        {item.user_name || item.user_id}
                                                    </Link>
                                                ) : (
                                                    '-'
                                                )}
                                                {item.owner_id && item.owner_id !== item.user_id && (
                                                    <>
                                                        <Box component="span" sx={{ color: '#949DB0' }}>/</Box>
                                                        <Link
                                                            component={RouterLink}
                                                            to={`/users/${item.owner_id}`}
                                                            sx={{ fontSize: '1.2rem' }}
                                                        >
                                                            {item.owner_name || item.owner_id}
                                                        </Link>
                                                    </>
                                                )}
                                            </Box>
                                        ) : (
                                            '-'
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={item.status}
                                            color={STATUS_COLOR[item.status] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center">{item.started_at || '-'}</TableCell>
                                    <TableCell align="center">{formatDuration(item.execution_time)}</TableCell>
                                    <TableCell align="center">
                                        <Typography sx={{ fontSize: '1rem', wordBreak: 'break-all', color: '#3B465C' }}>
                                            {item.guid}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                            {STOPPABLE.includes(item.status) && (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="error"
                                                    onClick={() => cancelFlow(item.guid)}
                                                >
                                                    Stop
                                                </Button>
                                            )}
                                            {item.polling_url && (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={() => fetchGetData(item)}
                                                >
                                                    Get data
                                                </Button>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    {!records.length && (
                        <Typography
                            sx={{
                                marginTop: '24px',
                                marginBottom: '24px',
                                textAlign: 'center',
                                fontSize: '2rem',
                                fontWeight: 500,
                                color: '#D76A6A',
                            }}
                        >
                            Items Not found
                        </Typography>
                    )}
                </TableContainer>

                <PaginationBar
                    count={totalRecords}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Box>
        </>
    );
};

export default BackgroundFlowsPage;
